// Mirrors backend/ms-payment/src/config/plans.config.ts (patient) and
// plan-quotas.config.ts. Prices are stored in millimes (1 DT = 1000 millimes)
// so amounts sent to /payment/initiate match exactly what ms-payment expects.
//
// i18n: the texts below (name, tagline, features) are the English defaults and
// stay in sync with the backend. Their translations live in the locale files
// under  plans.<patient|doctor>.<plan.key>.{name,tagline,features}
// PaymentBoard uses these defaults whenever a translation is missing.

export const BILLING_CYCLE = {
  MONTHLY: "monthly",
  YEARLY: "yearly",
};

export const DEFAULT_PLAN = "free";

export const PATIENT_PLANS = [
  {
    key: "free",
    name: "Free",
    priceMillimes: 0,
    currency: "TND",
    billing: BILLING_CYCLE.MONTHLY,
    tagline: "Get started with the essentials",
    quotas: {
      medicationCount: 5,
      fileShareMonthly: 3,
      storageMb: 100,
      relativeCount: 2,
      aiAssistantMonthly: 1,
    },
    features: [
      "Up to 5 medications tracked",
      "3 file shares / month",
      "100 MB medical record storage",
      "2 linked relatives",
      "1 AI assistant question / month",
    ],
  },
  {
    key: "basic",
    name: "Basic",
    priceMillimes: 5000,
    currency: "TND",
    billing: BILLING_CYCLE.MONTHLY,
    trialDays: 14,
    tagline: "For patients managing an ongoing condition",
    quotas: {
      medicationCount: 20,
      fileShareMonthly: 20,
      storageMb: 2048,
      relativeCount: 5,
      aiAssistantMonthly: null,
    },
    features: [
      "Up to 20 medications tracked",
      "20 file shares / month",
      "2 GB medical record storage",
      "5 linked relatives",
      "Unlimited AI assistant questions",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    priceMillimes: 12000,
    currency: "TND",
    billing: BILLING_CYCLE.MONTHLY,
    trialDays: 14,
    tagline: "Full access for complex care needs",
    highlighted: true,
    quotas: {
      medicationCount: null,
      fileShareMonthly: null,
      storageMb: 10240,
      relativeCount: null,
      aiAssistantMonthly: null,
    },
    features: [
      "Unlimited medications tracked",
      "Unlimited file shares",
      "10 GB medical record storage",
      "Unlimited linked relatives",
      "Unlimited AI assistant questions",
    ],
  },
];

export const DOCTOR_PLANS = [
  {
    key: "starter",
    name: "Starter",
    priceMillimes: 70000,
    currency: "TND",
    billing: BILLING_CYCLE.MONTHLY,
    trialDays: 14,
    tagline: "For solo practitioners getting set up",
    quotas: {
      patientCount: 30,
      storageMb: 5120,
      aiAssistantMonthly: 20,
    },
    features: [
      "Up to 30 active patients",
      "Basic AI diagnostic assistant",
      "5 GB medical record storage",
      "Email support",
    ],
  },
  {
    key: "pro",
    name: "Pro",
    priceMillimes: 130000,
    currency: "TND",
    billing: BILLING_CYCLE.MONTHLY,
    trialDays: 14,
    tagline: "For growing practices with more patients",
    highlighted: true,
    quotas: {
      patientCount: 150,
      storageMb: 25600,
      aiAssistantMonthly: null,
    },
    features: [
      "Up to 150 active patients",
      "Advanced AI diagnostic assistant",
      "25 GB medical record storage",
      "Voice assistant",
      "Priority support",
    ],
  },
  {
    key: "premium",
    name: "Premium",
    priceMillimes: 200000,
    currency: "TND",
    billing: BILLING_CYCLE.MONTHLY,
    trialDays: 14,
    tagline: "Full access for clinics and busy specialists",
    quotas: {
      patientCount: null,
      storageMb: 102400,
      aiAssistantMonthly: null,
    },
    features: [
      "Unlimited active patients",
      "Advanced AI diagnostic assistant",
      "100 GB medical record storage",
      "Voice assistant",
      "Priority support",
      "Dedicated account manager",
    ],
  },
];

export const getDoctorPlanByKey = (key) =>
  DOCTOR_PLANS.find((p) => p.key === key) || null;

export const millimesToAmount = (millimes) =>
  Number.isFinite(millimes) ? millimes / 1000 : 0;

// --- Localised formatting ---------------------------------------------------
// "ar-TN-u-nu-latn" keeps Latin digits (0-9), the usual convention in Tunisia.
const NUMBER_LOCALES = { fr: "fr-FR", ar: "ar-TN-u-nu-latn", en: "en-GB" };

// Display label of the currency per language (falls back to the ISO code).
const CURRENCY_LABELS = {
  fr: { TND: "DT" },
  ar: { TND: "د.ت" },
};

const baseLang = (lng) => String(lng || "en").split("-")[0];

export const formatAmount = (millimes, currency = "TND", lng = "en") => {
  const base = baseLang(lng);
  const value = new Intl.NumberFormat(NUMBER_LOCALES[base] || NUMBER_LOCALES.en, {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  }).format(millimesToAmount(millimes));
  const label = CURRENCY_LABELS[base]?.[currency] || currency;
  return `${value} ${label}`;
};

export const getPlanByKey = (key) =>
  PATIENT_PLANS.find((p) => p.key === key) || null;

// Pass the translated word as `unlimitedLabel`, e.g. t("payment.quota.unlimited")
export const formatQuota = (value, unit = "", unlimitedLabel = "Unlimited") => {
  if (value === null || value === undefined) return unlimitedLabel;
  return unit ? `${value} ${unit}` : String(value);
};